import { getLocalPath } from '../../utility/config/configuration'
import { showAlertBanner } from '../../../components/AlertBanner/index.js'
import { STATUS_ERROR } from '../../../components/AlertBanner/Constants.js'
import {
  FUND_PERFORMANCE_ERROR_MESSAGE_TEXT,
  ERROR_MESSAGE_TITLE,
} from '../investmentProducts/helpers/ErrorMessages.js'
import { safeFetch } from '../../../global/data/service.js'
const getApiBasePath = () => `${getLocalPath()}/halapi/documents`
const memoizePromiseFn = (fn) => {
  const cache = new Map()
  return (...args) => {
    const key = JSON.stringify(args)
    if (cache.has(key)) {
      return cache.get(key)
    }
    cache.set(
      key,
      fn(...args).catch((error) => {
        cache.delete(key)
        return Promise.reject(error)
      })
    )

    return cache.get(key)
  }
}
export const formatNewDate = (
  date,
  options = { month: 'short', day: 'numeric', year: 'numeric' }
) => {
  return new Intl.DateTimeFormat('en-US', options)?.format(date)
}

const fetcherFn = async (url, options = {}) => {
  try {
    const response = await safeFetch(url, options)
    if (!response.ok) {
      console.log(response.status, url)
      throw FUND_PERFORMANCE_ERROR_MESSAGE_TEXT
    }
    const jsonData = await response.json()
    return jsonData
  } catch (err) {
    showAlertBanner({
      status: STATUS_ERROR,
      title: ERROR_MESSAGE_TITLE,
      text: FUND_PERFORMANCE_ERROR_MESSAGE_TEXT,
    })
  }
}

export const fetcher = memoizePromiseFn(fetcherFn)

export const getDistributionReports = async () => {
  const url = `${getApiBasePath()}?_scope=/content/documents/cti-advisor/resources/tax-center/capital-gains-and-dividends&_fields=title,content&_limit=-1`
  const data = (await fetcher(url))?._embedded?.documents || []
  const distributions = data?.reduce((t, n) => {
    const reports = n?.content?.reduce(
      (p, i) =>
        i?.text
          ? [
              ...p,
              {
                name: i?.text,
                url: i?.Link?.[0]?.string,
              },
            ]
          : p,
      []
    )
    return [
      ...t,
      {
        title: n?.title,
        reports,
      },
    ]
  }, [])
  return {
    distributions,
  }
}

export const getPageIds = async (docPaths = []) => {
  const data = await fetcher(
    `/restapi/frontendutility/getpageid?count=${docPaths?.length}`,
    {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
      },
      body: JSON.stringify({ docPaths }),
    }
  )
  return data?.results || {}
}

const getSubscriptionAndFund = async () => {
  const subscriptionsAndFundData = {
    funds: [],
    subscriptions: [],
  }
  const url = `${getApiBasePath()}?_scope=/content/documents/cti-advisor/subscriptions&_limit=-1`
  const data = (await fetcher(url))?._embedded?.documents || []
  data?.forEach((i) => {
    if (i?._meta?.type === 'cti:FundSubscriptionType') {
      subscriptionsAndFundData?.funds.push(i)
    } else if (i?._meta?.type === 'cti:SubscriptionType') {
      subscriptionsAndFundData?.subscriptions?.push(i)
    }
  })

  return subscriptionsAndFundData
}

export const getFundSubscription = async () => {
  const _scope = '/content/documents/cti-advisor/subscriptions/funds'
  const data = (await getSubscriptionAndFund())?.funds
  const fundIds = data?.reduce((p, i) => {
    const docpath = i?._meta?.path?.replaceAll('funds/', '')
    const cpath = `${_scope}/${docpath}/${docpath}`
    const getLiteratureIds = (l) =>
      l?.LiteratureCompound?.reduce(
        (o, _, j) => ({
          ...o,
          [`${docpath}__${j}`]: `${cpath}/cti:availableLiterature${
            j === 0 ? '' : `[${j + 1}]`
          }`,
        }),
        {}
      )
    return { ...p, [docpath]: cpath, ...getLiteratureIds(i) }
  }, {})

  const fundPageIds = await getPageIds(Object.values(fundIds))
  const getPathId = (p) =>
    fundPageIds[fundIds[p?.replaceAll('funds/', '')]] ?? ''

  const result = data?.reduce(
    (p, n) => [
      ...p,
      {
        name: n?.name,
        id: getPathId(n?._meta?.path),
        cusip: n?.cusip,
        selected: false,
        active: true,
        productType: n?.productType,
        assetClass: n?.dynamicdropdown,
        availableRoles: n?.xavailableroles,
        literature: n?.LiteratureCompound?.reduce(
          (t, i, ind) => [
            ...t,
            {
              name: i?.name,
              id: `${getPathId(n?._meta?.path)}${getPathId(
                `${n?._meta?.path}__${ind}`
              )}`,
              availableRoles: i?.xavailableroles,
              frequency: i?.staticdropdown,
              wholesalerLabel: i?.wholesalerLabel,
              selected: false,
              active: i?.active,
            },
          ],
          []
        ),
      },
    ],
    []
  )
  return {
    funds: result,
  }
}

export const getSubscriptions = async () => {
  const _scope = '/content/documents/cti-advisor/subscriptions'
  const data = (await getSubscriptionAndFund())?.subscriptions
  const subsIds = data?.reduce((p, i) => {
    const lastName = i?._meta?.path?.split('/')
    return {
      ...p,
      [i?._meta?.path]: `${_scope}/${i?._meta?.path}/${
        lastName[lastName?.length - 1]
      }`,
    }
  }, {})

  const subsPageIds = await getPageIds(Object.values(subsIds))
  const getPathId = (p) => subsPageIds[subsIds[p]] ?? ''

  const result = data?.reduce(
    (p, n) => [
      ...p,
      {
        id: getPathId(n?._meta?.path),
        name: n?.name,
        category: n?.category,
        description: n?.description,
        frequency: n?.frequency || '',
        availableRoles: n?.xavailableroles,
        status: n?.status,
        previewLink: n?.previewLink?.Link?.[0]._meta?.mirror?.path
          ? `binaries/${n?.previewLink?.Link?.[0]._meta?.mirror?.path}`
          : '',
        wholesalerLabel: n?.wholesalerLabel,
      },
    ],
    []
  )

  return {
    subscriptions: result,
  }
}
